import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../Config";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";

const Direccion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { baseUrl } = useGlobalContext();

  const [loading, setLoading] = useState(true);
  const [direccion, setDireccion] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let abort = false;

    const load = async () => {
      if (!baseUrl || !id) return;

      setLoading(true);
      setError("");

      try {
        const resp = await fetch(`${baseUrl}direcciones/${id}`, {
          credentials: "include",
        });

        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || "Error cargando dirección");
        }

        const data = await resp.json();
        const d = data.item;

        if (!d) throw new Error("Formato de respuesta inválido");

        if (!abort) setDireccion(d);
      } catch (e) {
        console.error(e);
        if (!abort) setError(e.message || "No se pudo cargar la dirección");
      } finally {
        if (!abort) setLoading(false);
      }
    };

    load();
    return () => {
      abort = true;
    };
  }, [baseUrl, id]);

  // Título: siempre calle + número, si existen
  const headerName = useMemo(() => {
    if (!direccion) return "—";
    const calleNum = `${direccion.calle ?? ""} ${
      direccion.numero ?? ""
    }`.trim();
    if (calleNum) return calleNum;
    if (direccion.direccion) return direccion.direccion;
    return "Dirección";
  }, [direccion]);

  const tipoDescripcion =
    direccion?.tipoDireccion?.descripcion ||
    direccion?.tipoDireccion?.codigo ||
    null;

  const mapSrc = useMemo(() => {
    if (!direccion) return null;

    const { lat, lng } = direccion;
    if (lat != null && lng != null) {
      return `https://www.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
    }

    const q =
      direccion.direccion ||
      `${direccion.calle ?? ""} ${direccion.numero ?? ""}, ${
        direccion.comuna ?? ""
      }, ${direccion.ciudad ?? ""}, ${direccion.pais ?? ""}`.trim();

    if (!q) return null;

    return `https://www.google.com/maps?q=${encodeURIComponent(
      q
    )}&z=16&output=embed`;
  }, [direccion]);

  const renderDetalle = (label, value) => (
    <div
      key={label}
      className="flex justify-between gap-3 py-2 border-bottom-1 border-100"
    >
      <span className="text-sm text-color-secondary">{label}</span>
      <span className="text-sm font-medium text-right">
        {value ?? <span className="text-400">—</span>}
      </span>
    </div>
  );

  return (
    <div className="p-4 md:p-5">
      {/* Encabezado */}
      <div className="flex flex-wrap gap-3 items-start mb-3">
        <Button
          icon="pi pi-arrow-left"
          rounded
          outlined
          onClick={() => navigate(-1)}
        />

        <div className="surface-200 text-color-secondary flex items-center justify-center w-8rem h-8rem border-circle">
          <i className="pi pi-map-marker text-5xl" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Título grande */}
          <div className="text-3xl md:text-4xl lg:text-5xl font-bold truncate">
            {loading ? <Skeleton width="60%" height="2.5rem" /> : headerName}
          </div>

          {/* Tags (país, comuna/ciudad) */}
          {!loading && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {direccion?.pais && (
                <Tag
                  value={direccion.pais}
                  icon="pi pi-globe"
                  severity="info"
                  className="text-base px-3 py-2"
                />
              )}

              {direccion?.comuna && direccion?.ciudad && (
                <Tag
                  value={`${direccion.comuna}, ${direccion.ciudad}`}
                  icon="pi pi-map"
                  severity="secondary"
                  className="text-base px-3 py-2"
                />
              )}
            </div>
          )}

          {/* Detalles listados debajo del nombre */}
          {!loading && direccion && (
            <div className="mt-4">
              {renderDetalle(
                "Dirección",
                direccion.direccion ||
                  `${direccion.calle ?? ""} ${direccion.numero ?? ""}`.trim()
              )}
              {renderDetalle("Comuna", direccion.comuna)}
              {renderDetalle("Ciudad", direccion.ciudad)}
              {renderDetalle("Región", direccion.region)}
              {renderDetalle("País", direccion.pais)}
            </div>
          )}

          {/* Skeleton de detalles mientras carga */}
          {loading && (
            <div className="mt-4">
              <Skeleton width="80%" className="mb-2" />
              <Skeleton width="60%" className="mb-2" />
              <Skeleton width="90%" className="mb-2" />
              <Skeleton width="50%" className="mb-2" />
            </div>
          )}
        </div>
      </div>

      <Divider className="my-4" />

      {error && (
        <Card className="mb-3 border-red-200">
          <div className="text-red-500 text-sm">{error}</div>
        </Card>
      )}

      {/* Mapa (solo) */}
      <div className="grid">
        <div className="col-12">
          <Card className="mb-3 h-full">
            <div className="text-base text-color-secondary mb-3 font-medium">
              Mapa
            </div>

            {loading && (
              <Skeleton width="100%" height="18rem" className="mb-2" />
            )}

            {!loading && mapSrc && (
              <div className="w-full" style={{ minHeight: "18rem" }}>
                <iframe
                  title="Mapa dirección"
                  src={mapSrc}
                  width="100%"
                  height="320"
                  style={{ border: 0, borderRadius: "1rem" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}

            {!loading && !mapSrc && (
              <div className="surface-100 p-4 rounded-2xl text-color-secondary text-sm">
                No se pudo generar el mapa para esta dirección.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Direccion;
